// AuthGuard — calls GET /api/auth/check on mount, redirects unauthenticated
// users to /login?next=<current-path>. Wraps protected routes so the React
// guard runs before any page-specific content renders (Component 22 layer 2).
//
// While authenticated it also:
//  • runs the 30-min inactivity timer (useInactivityTimeout)
//  • listens for ophelia:unauthorized events dispatched by apiFetch on any
//    unexpected 401, triggering an immediate redirect to /login

import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { apiFetch, apiJson } from "../api/client";
import type { AppSettings } from "../api/types";
import { changeLanguage } from "../i18n/i18n";
import { useInactivityTimeout } from "./useInactivityTimeout";

type GuardState = "checking" | "ok" | "denied";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GuardState>("checking");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/auth/check")
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) {
          setState("denied");
          return;
        }
        // Sync UI language to the persisted setting before any protected
        // route renders — avoids a flash of English content when German is
        // the persisted choice.
        try {
          const settings = await apiJson<AppSettings>("/api/settings/");
          if (!cancelled && settings.ui_language) {
            await changeLanguage(settings.ui_language);
          }
        } catch {
          // Non-fatal; keep the default language.
        }
        if (!cancelled) setState("ok");
      })
      .catch(() => {
        if (!cancelled) setState("denied");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Redirect to /login whenever any API call returns 401 unexpectedly
  // (e.g. server restarted and wiped sessions while the user had the app open).
  useEffect(() => {
    if (state !== "ok") return;
    function onUnauthorized() {
      navigate("/login", { replace: true });
    }
    window.addEventListener("ophelia:unauthorized", onUnauthorized);
    return () => window.removeEventListener("ophelia:unauthorized", onUnauthorized);
  }, [state, navigate]);

  // 30-min inactivity timeout + tab-close logout. Only active once auth passes.
  useInactivityTimeout(state === "ok");

  if (state === "checking") {
    // Intentional blank — the guard runs in <100 ms on a warm server, and a
    // spinner would flash distractingly. Final-mile UX in Unit 5.
    return null;
  }

  if (state === "denied") {
    const next = `${location.pathname}${location.search}`;
    const target = next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
